import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import {
  Users, TrendingUp, UserCheck, UserX, AlertTriangle, Loader2, Zap, Star,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEGMENT_CONFIG = {
  engaged: { color: "#10b981", bg: "bg-emerald-500/10", text: "text-emerald-500", label: "Engaged", icon: UserCheck },
  at_risk: { color: "#f59e0b", bg: "bg-amber-500/10", text: "text-amber-500", label: "At Risk", icon: AlertTriangle },
  inactive: { color: "#ef4444", bg: "bg-red-500/10", text: "text-red-500", label: "Inactive", icon: UserX },
};

export function EngagementScoring({ darkMode: dm }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };

  const fetchScores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-analytics/engagement-scores`, { headers });
      setData(res.data);
    } catch { toast.error("Failed to load engagement scores"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchScores(); }, [fetchScores]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data || data.total_scored === 0) {
    return (
      <div className={`text-center py-16 rounded-2xl border-2 border-dashed ${dm ? "border-gray-700" : "border-gray-200"}`}>
        <Zap className={`w-10 h-10 mx-auto mb-3 ${dm ? "text-gray-600" : "text-gray-300"}`} />
        <p className={`text-sm font-medium ${dm ? "text-gray-400" : "text-gray-500"}`}>No engagement data yet</p>
        <p className={`text-xs mt-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>
          Scores will appear after sending emails and tracking opens/clicks
        </p>
      </div>
    );
  }

  const { segments, total_scored, avg_score, subscribers } = data;

  const pieData = [
    { name: "Engaged", value: segments.engaged, color: SEGMENT_CONFIG.engaged.color },
    { name: "At Risk", value: segments.at_risk, color: SEGMENT_CONFIG.at_risk.color },
    { name: "Inactive", value: segments.inactive, color: SEGMENT_CONFIG.inactive.color },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-5" data-testid="engagement-scoring">
      {/* Header */}
      <div>
        <h3 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>
          Engagement Scoring
        </h3>
        <p className={`text-xs mt-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
          Subscribers scored by open rate, click rate, recency, and bounce history
        </p>
      </div>

      {/* Segment Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Segment Cards */}
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <SegmentCard dm={dm} label="Total Scored" value={total_scored} icon={Users} color="text-blue-500" bg="bg-blue-500/10" testId="seg-total" />
          <SegmentCard dm={dm} label="Avg Score" value={avg_score} icon={Star} color="text-indigo-500" bg="bg-indigo-500/10" testId="seg-avg" />
          {Object.entries(SEGMENT_CONFIG).map(([key, cfg]) => (
            <SegmentCard
              key={key}
              dm={dm}
              label={cfg.label}
              value={segments[key]}
              icon={cfg.icon}
              color={cfg.text}
              bg={cfg.bg}
              testId={`seg-${key}`}
              pct={total_scored > 0 ? Math.round(segments[key] / total_scored * 100) : 0}
            />
          ))}
        </div>

        {/* Pie Chart */}
        <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
          <CardContent className="p-3 flex items-center justify-center">
            <div className="h-[160px] w-full" data-testid="segment-pie-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={60}
                    paddingAngle={3}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: dm ? "#1f2937" : "#fff",
                      border: `1px solid ${dm ? "#374151" : "#e5e7eb"}`,
                      borderRadius: "10px",
                      color: dm ? "#f3f4f6" : "#111827",
                      fontSize: 11,
                    }}
                    formatter={(value, name) => [`${value} subscribers`, name]}
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscriber Table */}
      <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
            <TrendingUp className="w-4 h-4 inline mr-1.5" />
            Subscriber Scores
          </CardTitle>
          <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
            Individual engagement scores ranked from highest to lowest
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="engagement-table">
              <thead>
                <tr className={`border-b ${dm ? "border-gray-700" : "border-gray-100"}`}>
                  <th className={`text-left py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Subscriber</th>
                  <th className={`text-center py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Score</th>
                  <th className={`text-center py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Segment</th>
                  <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Sent</th>
                  <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Open %</th>
                  <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Click %</th>
                  <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Bounces</th>
                  <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.map((sub, i) => {
                  const cfg = SEGMENT_CONFIG[sub.segment] || SEGMENT_CONFIG.inactive;
                  return (
                    <tr
                      key={i}
                      className={`border-b transition-colors ${dm ? "border-gray-700/50 hover:bg-gray-700/30" : "border-gray-50 hover:bg-gray-50"}`}
                      data-testid={`score-row-${i}`}
                    >
                      <td className={`py-2.5 px-3 font-mono text-xs ${dm ? "text-gray-200" : "text-gray-800"}`}>
                        {sub.email}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <ScoreBadge score={sub.score} dm={dm} />
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <Badge className={`text-[9px] px-1.5 ${cfg.bg} ${cfg.text}`}>
                          {cfg.label}
                        </Badge>
                      </td>
                      <td className={`py-2.5 px-3 text-right font-mono ${dm ? "text-gray-300" : "text-gray-700"}`}>{sub.sent}</td>
                      <td className={`py-2.5 px-3 text-right font-mono ${sub.open_rate >= 30 ? "text-emerald-500" : sub.open_rate >= 15 ? "text-amber-500" : dm ? "text-gray-400" : "text-gray-500"}`}>
                        {sub.open_rate}%
                      </td>
                      <td className={`py-2.5 px-3 text-right font-mono ${sub.click_rate >= 10 ? "text-emerald-500" : sub.click_rate >= 5 ? "text-amber-500" : dm ? "text-gray-400" : "text-gray-500"}`}>
                        {sub.click_rate}%
                      </td>
                      <td className={`py-2.5 px-3 text-right font-mono ${sub.bounced > 0 ? "text-red-500" : dm ? "text-gray-500" : "text-gray-400"}`}>
                        {sub.bounced}
                      </td>
                      <td className={`py-2.5 px-3 text-right ${dm ? "text-gray-500" : "text-gray-400"}`}>
                        {sub.last_activity ? new Date(sub.last_activity).toLocaleDateString() : "\u2014"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function SegmentCard({ dm, label, value, icon: Icon, color, bg, testId, pct }) {
  return (
    <Card className={`rounded-xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`} data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bg}`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
          <div>
            <p className={`text-[10px] font-medium uppercase tracking-wider ${dm ? "text-gray-400" : "text-gray-500"}`}>{label}</p>
            <div className="flex items-baseline gap-1.5">
              <p className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>{value}</p>
              {pct !== undefined && <span className={`text-[10px] ${dm ? "text-gray-500" : "text-gray-400"}`}>{pct}%</span>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function ScoreBadge({ score, dm }) {
  let bg, text;
  if (score >= 50) { bg = "bg-emerald-500"; text = "text-white"; }
  else if (score >= 20) { bg = "bg-amber-500"; text = "text-white"; }
  else { bg = "bg-red-500"; text = "text-white"; }

  return (
    <span className={`inline-flex items-center justify-center w-8 h-5 rounded-md text-[10px] font-bold ${bg} ${text}`}>
      {score}
    </span>
  );
}
